import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Lock, Copy, CheckCircle2, Upload, FileText, Save, Trash2, Code2, Landmark, Palette, Minus, GraduationCap, Crown, BookOpen, Layers, HeartPulse, Wrench, FileDown, Linkedin, Link2 } from "lucide-react";
import { motion } from "framer-motion";

function htmlToText(html) {
  const tmp = document.createElement("div"); tmp.innerHTML = html || "";
  return (tmp.textContent || "").replace(/\s+\n/g, "\n").trim();
}

const TEMPLATES = [
  { key: "modern",     label: "Modern",     icon: Layers,        bg: "bg-[#4338ca] text-white", desc: "Balanced impact + outcomes. Confident, current voice." },
  { key: "tech",       label: "Tech",       icon: Code2,         bg: "bg-[#0ea5e9] text-white", desc: "Stack-first, systems thinking, engineering metrics." },
  { key: "finance",    label: "Finance",    icon: Landmark,      bg: "bg-[#0f766e] text-white", desc: "Quantitative — $, %, bps, deal sizes, risk reduced." },
  { key: "creative",   label: "Creative",   icon: Palette,       bg: "bg-[#ec4899] text-white", desc: "Narrative-driven with brand & audience impact." },
  { key: "healthcare", label: "Healthcare", icon: HeartPulse,    bg: "bg-[#0891b2] text-white", desc: "Patient outcomes, compliance, EHR, clinical protocols." },
  { key: "trades",     label: "Skilled Trades", icon: Wrench,    bg: "bg-[#d97706] text-white", desc: "Licenses, safety, hands-on scope, code compliance." },
  { key: "minimalist", label: "Minimalist", icon: Minus,         bg: "bg-white",                 desc: "One-line bullets, verbs + results, zero filler." },
  { key: "academic",   label: "Academic",   icon: BookOpen,      bg: "bg-[#7c3aed] text-white", desc: "Research, publications, methods, teaching, peer review." },
  { key: "executive",  label: "Executive",  icon: Crown,         bg: "bg-[#0A0A0A] text-white", desc: "Strategy, P&L, transformation, decisive leadership." },
  { key: "student",    label: "Student",    icon: GraduationCap, bg: "bg-[#eab308]",             desc: "Projects, coursework, potential, quick learner." },
];

export default function ResumeTailor() {
  const { user } = useAuth();
  const loc = useLocation();
  const preJob = loc.state?.job;
  const [resume, setResume] = useState("");
  const [jobDesc, setJobDesc] = useState(preJob?.description ? htmlToText(preJob.description) : "");
  const [jobTitle, setJobTitle] = useState(preJob?.title || "");
  const [company, setCompany] = useState(preJob?.company || "");
  const [template, setTemplate] = useState("modern");
  const [busy, setBusy] = useState(false);
  const [gapBusy, setGapBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingResume, setSavingResume] = useState(false);
  const [savedLength, setSavedLength] = useState(0);
  const [result, setResult] = useState(null);
  const [gapResult, setGapResult] = useState(null);
  const [mode, setMode] = useState("free"); // free | pro
  const [showLinkedIn, setShowLinkedIn] = useState(false);
  const [liUrl, setLiUrl] = useState("");
  const [liPaste, setLiPaste] = useState("");
  const [liBusy, setLiBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const fileRef = useRef(null);

  const isPro = user?.plan === "pro";

  // Load saved resume once
  useEffect(() => {
    if (!user) return;
    api.get("/resume").then(({ data }) => {
      if (data.text) { setResume(data.text); setSavedLength(data.text.length); }
    }).catch(() => {});
  }, [user]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/resume/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResume(data.text);
      setSavedLength(data.length);
      toast.success(`Extracted ${data.length.toLocaleString()} characters — saved to your profile`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveResume = async () => {
    setSavingResume(true);
    try {
      const { data } = await api.put("/resume", { text: resume });
      setSavedLength(data.length);
      toast.success("Resume saved to your profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingResume(false); }
  };

  const clearResume = async () => {
    if (!window.confirm("Delete saved resume?")) return;
    try {
      await api.delete("/resume");
      setSavedLength(0); setResume("");
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const run = async () => {
    if (!isPro) { toast.error("Upgrade to Pro to use AI tailoring"); return; }
    if (!resume.trim() || !jobDesc.trim()) { toast.error("Add both resume and job description"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/tailor", { resume, job_description: jobDesc, job_title: jobTitle, company, template });
      setResult(data);
      toast.success(`Resume tailored — ${template} template`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally { setBusy(false); }
  };

  const runGap = async () => {
    if (!resume.trim() || !jobDesc.trim()) { toast.error("Add both resume and job description"); return; }
    setGapBusy(true); setGapResult(null);
    try {
      const { data } = await api.post("/ai/gap-analysis", { resume, job_description: jobDesc });
      setGapResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gap analysis failed");
    } finally { setGapBusy(false); }
  };

  const copyAll = () => {
    if (!result) return;
    const text = [
      `SUMMARY\n${result.summary || ""}`,
      `\nMATCH SCORE: ${result.match_score}%`,
      `\nBULLETS\n${(result.tailored_bullets || []).map((b) => `• ${b}`).join("\n")}`,
      `\nMATCHED KEYWORDS: ${(result.matched_keywords || []).join(", ")}`,
      `\nMISSING KEYWORDS: ${(result.missing_keywords || []).join(", ")}`,
      `\nRECOMMENDATIONS\n${(result.recommendations || []).map((r) => `• ${r}`).join("\n")}`,
    ].join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const download = async (format) => {
    if (!result) return;
    try {
      const res = await api.post("/export/resume", {
        format,
        candidate_name: user?.name || (user?.email ? user.email.split("@")[0] : "Resume"),
        contact_line: user?.email || "",
        summary: result.summary || "",
        bullets: result.tailored_bullets || [],
        matched_keywords: result.matched_keywords || [],
        job_title: jobTitle || undefined,
        company: company || undefined,
        template: result.template || template,
      }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (user?.name || "resume").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.href = url; a.download = `${safe}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not download ${format.toUpperCase()}`);
    }
  };

  const linkedinImport = async () => {
    if (!liUrl.trim() && !liPaste.trim()) { toast.error("Paste a LinkedIn URL or your profile text"); return; }
    setLiBusy(true);
    try {
      const { data } = await api.post("/linkedin/import", { url: liUrl || null, pasted_text: liPaste || null });
      setResume(data.text);
      setSavedLength(data.length);
      setShowLinkedIn(false); setLiUrl(""); setLiPaste("");
      toast.success(`Imported from LinkedIn (${data.length.toLocaleString()} chars) — saved to your profile`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setLiBusy(false); }
  };

  const share = async () => {
    if (!result) return;
    setSharing(true);
    try {
      const { data } = await api.post("/share/resume", {
        summary: result.summary || "",
        bullets: result.tailored_bullets || [],
        matched_keywords: result.matched_keywords || [],
        missing_keywords: result.missing_keywords || [],
        recommendations: result.recommendations || [],
        match_score: result.match_score,
        job_title: jobTitle || undefined,
        company: company || undefined,
        template: result.template || template,
        candidate_name: user?.name || undefined,
        contact_line: user?.email || undefined,
      });
      setShareUrl(data.url);
      navigator.clipboard.writeText(data.url).catch(() => {});
      toast.success("Share link created & copied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally { setSharing(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">AI Resume Tailor</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Tailor to any job in seconds</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">Upload once. Reuse everywhere. Claude Sonnet rewrites bullets, injects keywords, and scores your match.</p>
        </div>
        {!isPro && (
          <Link to="/pricing" data-testid="tailor-upgrade-cta" className="nb-btn bg-[#ec4899] text-white px-5 py-2.5 inline-flex items-center gap-2">
            <Lock className="w-4 h-4" /> Unlock with Pro — $19/mo
          </Link>
        )}
      </div>

      {/* Upload + save row */}
      <div className="nb-card p-5 mb-6 flex flex-wrap items-center gap-3">
        <input ref={fileRef} data-testid="resume-file-input" type="file" accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" onChange={handleUpload} className="hidden" />
        <button data-testid="resume-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="nb-btn bg-[#4338ca] text-white px-4 py-2 inline-flex items-center gap-2">
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Extracting…</> : <><Upload className="w-4 h-4" /> Upload PDF / DOCX</>}
        </button>
        <button data-testid="linkedin-import-btn" onClick={() => setShowLinkedIn(true)}
          className="nb-btn bg-[#0a66c2] text-white px-4 py-2 inline-flex items-center gap-2">
          <Linkedin className="w-4 h-4" /> Import from LinkedIn
        </button>
        <div className="text-sm text-gray-600">
          {savedLength > 0
            ? <span className="inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#22c55e]" /> Resume saved · {savedLength.toLocaleString()} chars</span>
            : <span>No resume saved yet — upload, import from LinkedIn, or paste text below and save.</span>}
        </div>
        <div className="ml-auto flex gap-2">
          <button data-testid="resume-save-btn" onClick={saveResume} disabled={savingResume || !resume.trim()}
            className="nb-btn bg-white px-3 py-2 text-sm inline-flex items-center gap-1.5">
            {savingResume ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          {savedLength > 0 && (
            <button data-testid="resume-clear-btn" onClick={clearResume} className="nb-btn bg-white px-3 py-2 text-sm inline-flex items-center gap-1.5">
              <Trash2 className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
      </div>

      {showLinkedIn && (
        <div className="nb-card p-5 mb-6" data-testid="linkedin-modal">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-bold">
              <Linkedin className="w-5 h-5 text-[#0a66c2]" /> Import from LinkedIn
            </div>
            <button onClick={() => setShowLinkedIn(false)} className="nb-pill">close</button>
          </div>
          <p className="text-sm text-gray-600">LinkedIn blocks anonymous scraping, so best results come from pasting your profile text. Tip: on your LinkedIn profile click <strong>More → Save to PDF</strong>, then open the PDF and paste the text below.</p>
          <div className="mt-3 space-y-3">
            <input data-testid="li-url" value={liUrl} onChange={(e) => setLiUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/your-handle (optional — we'll try to fetch)"
              className="w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#0a66c2]" />
            <textarea data-testid="li-paste" value={liPaste} onChange={(e) => setLiPaste(e.target.value)} rows={8}
              placeholder="…or paste your LinkedIn profile text here (recommended)"
              className="w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#0a66c2] text-sm" />
            <button data-testid="li-import" onClick={linkedinImport} disabled={liBusy}
              className="nb-btn bg-[#0a66c2] text-white px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
              {liBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Linkedin className="w-4 h-4" /> Import to my profile</>}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-bold">Your inputs</h2>
            <div className="inline-flex border-2 border-black rounded-full overflow-hidden" data-testid="mode-tabs">
              <button data-testid="mode-free" onClick={() => setMode("free")}
                className={`px-3 py-1.5 text-xs font-black ${mode === "free" ? "bg-[#22c55e] text-white" : "bg-white"}`}>
                Free · Gap analysis
              </button>
              <button data-testid="mode-pro" onClick={() => setMode("pro")}
                className={`px-3 py-1.5 text-xs font-black border-l-2 border-black ${mode === "pro" ? "bg-[#4338ca] text-white" : "bg-white"}`}>
                Pro · AI Tailor
              </button>
            </div>
          </div>

          {mode === "pro" && (
            <div className="mt-4">
              <div className="label-eyebrow">Resume template</div>
              <div data-testid="template-picker" className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TEMPLATES.map((t) => {
                  const active = template === t.key;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`template-${t.key}`}
                      onClick={() => setTemplate(t.key)}
                      aria-pressed={active}
                      title={t.desc}
                      className={`text-left border-2 border-black rounded-xl p-3 transition-transform ${active ? `${t.bg} shadow-[4px_4px_0_0_#0A0A0A] -translate-y-0.5` : "bg-white hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_#0A0A0A]"}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon className="w-4 h-4" strokeWidth={2.5} />
                        <span className="font-black text-sm">{t.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">{TEMPLATES.find((t) => t.key === template)?.desc}</p>
            </div>
          )}

          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="tailor-job-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Job title (optional)"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
              <input data-testid="tailor-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company (optional)"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Resume (paste or upload above)</label>
              <textarea data-testid="tailor-resume" value={resume} onChange={(e) => setResume(e.target.value)} rows={10}
                placeholder="Paste your resume as plain text, or upload a PDF/DOCX above…"
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] font-mono text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Job description</label>
              <textarea data-testid="tailor-job-desc" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} rows={10}
                placeholder="Paste the target job description…"
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] font-mono text-sm" />
            </div>
            <button data-testid="tailor-run" onClick={mode === "pro" ? run : runGap} disabled={mode === "pro" ? busy : gapBusy}
              className={`nb-btn ${mode === "pro" ? "bg-[#4338ca]" : "bg-[#22c55e]"} text-white px-5 py-3 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60`}>
              {mode === "pro"
                ? (busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Tailoring…</> : <><Sparkles className="w-4 h-4" /> Tailor with AI (Pro)</>)
                : (gapBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><CheckCircle2 className="w-4 h-4" /> Run free gap analysis</>)}
            </button>
            {mode === "pro" && !isPro && <p className="text-xs text-gray-500 text-center">Requires Pro subscription — $19/mo.</p>}
            {mode === "free" && <p className="text-xs text-gray-500 text-center">Instant. No AI cost. Try Pro for full tailored bullets.</p>}
          </div>
        </div>

        <div className="nb-card p-6 min-h-[300px]" data-testid="tailor-result">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold">{mode === "pro" ? "AI output" : "Gap analysis"}</h2>
              {mode === "pro" && result && (
                <span data-testid="result-template-badge" className={`nb-pill ${TEMPLATES.find((t) => t.key === (result.template || template))?.bg || "bg-white"}`}>
                  {(result.template || template).toUpperCase()}
                </span>
              )}
              {mode === "free" && (
                <span className="nb-pill bg-[#22c55e] text-white">FREE</span>
              )}
            </div>
            {mode === "pro" && result && (
              <div className="flex items-center gap-1.5">
                <button data-testid="export-pdf" onClick={() => download("pdf")} className="nb-btn bg-[#4338ca] text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> PDF</button>
                <button data-testid="export-docx" onClick={() => download("docx")} className="nb-btn bg-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> DOCX</button>
                <button data-testid="share-resume" onClick={share} disabled={sharing}
                  className="nb-btn bg-[#ec4899] text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5">
                  {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Share
                </button>
                <button data-testid="tailor-copy" onClick={copyAll} className="nb-btn bg-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy</button>
              </div>
            )}
          </div>

          {mode === "free" ? (
            !gapResult ? (
              <div className="mt-8 text-center text-gray-500">
                <CheckCircle2 className="w-10 h-10 mx-auto text-[#22c55e]" />
                <p className="mt-3 font-bold">Your fit score & missing keywords will appear here.</p>
                <p className="text-xs mt-1">Paste a resume + a job description, then hit Run.</p>
              </div>
            ) : (
              <div className="mt-4 space-y-5" data-testid="gap-result">
                <div className="flex items-center gap-4">
                  <div className="relative w-24 h-24 shrink-0">
                    <div className={`absolute inset-0 rounded-full border-2 border-black flex items-center justify-center ${gapResult.match_score >= 75 ? "bg-[#22c55e]" : gapResult.match_score >= 50 ? "bg-[#eab308]" : "bg-[#ec4899]"}`}>
                      <div className="text-3xl font-black text-white">{gapResult.match_score}<span className="text-sm">%</span></div>
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Fit score</div>
                    <p className="text-sm text-gray-700 mt-1">You match <strong>{gapResult.matched_count}</strong> of <strong>{gapResult.total_keywords_in_jd}</strong> keywords in this job description.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="label-eyebrow">Matched</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(gapResult.matched_keywords || []).map((k) => <span key={k} className="nb-pill bg-[#22c55e] text-white text-[11px]">{k}</span>)}
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Missing — add if you can</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(gapResult.missing_keywords || []).map((k) => <span key={k} className="nb-pill bg-[#ec4899] text-white text-[11px]">{k}</span>)}
                    </div>
                  </div>
                </div>
                {!isPro && (
                  <div className="mt-4 border-2 border-black rounded-lg p-4 bg-[#FEFCE8]">
                    <div className="font-bold">Want the full rewrite?</div>
                    <p className="text-sm text-gray-700 mt-1">Pro rewrites your bullets in 8 templates, adds a tailored summary, and generates cover letters. $19/mo, cancel anytime.</p>
                    <Link to="/pricing" data-testid="gap-upgrade-cta" className="mt-3 nb-btn bg-[#4338ca] text-white px-4 py-2 text-sm inline-flex">Upgrade to Pro</Link>
                  </div>
                )}
              </div>
            )
          ) : !result ? (
            <div className="mt-8 text-center text-gray-500">
              <Sparkles className="w-10 h-10 mx-auto text-[#eab308]" />
              <p className="mt-3 font-bold">Your tailored resume will appear here.</p>
            </div>
          ) : (
            <>
              {shareUrl && (
                <div data-testid="share-url-banner" className="mt-3 mb-3 border-2 border-black rounded-lg p-3 bg-[#FEFCE8] flex flex-wrap items-center gap-2">
                  <Link2 className="w-4 h-4 text-[#ec4899]" />
                  <span className="text-xs font-bold uppercase tracking-widest">Public share link</span>
                  <input readOnly value={shareUrl} className="flex-1 min-w-[240px] border-2 border-black rounded-md px-2 py-1 bg-white font-mono text-xs" />
                  <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="nb-pill">copy</button>
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="nb-pill bg-[#4338ca] text-white">open</a>
                </div>
              )}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 space-y-5">
              <div className="flex items-center gap-4">
                <div className="relative w-24 h-24 shrink-0">
                  <div className="absolute inset-0 rounded-full border-2 border-black bg-[#22c55e] flex items-center justify-center">
                    <div className="text-3xl font-black text-white">{result.match_score ?? "—"}<span className="text-sm">%</span></div>
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Match score</div>
                  <p className="text-sm text-gray-700 mt-1">{result.summary}</p>
                </div>
              </div>

              <div>
                <div className="label-eyebrow">Tailored bullets</div>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {(result.tailored_bullets || []).map((b, i) => (
                    <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#22c55e] mt-0.5 shrink-0" strokeWidth={2.5} /><span>{b}</span></li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="label-eyebrow">Matched keywords</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(result.matched_keywords || []).map((k) => <span key={k} className="nb-pill bg-[#22c55e] text-white text-[11px]">{k}</span>)}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Missing keywords</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(result.missing_keywords || []).map((k) => <span key={k} className="nb-pill bg-[#ec4899] text-white text-[11px]">{k}</span>)}
                  </div>
                </div>
              </div>

              {result.recommendations?.length > 0 && (
                <div>
                  <div className="label-eyebrow">Recommendations</div>
                  <ul className="mt-2 space-y-1 text-sm list-disc ml-5">
                    {result.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
